import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import type { ReduxState } from '../store';
import { type EnumsStore, loadEnum } from '../store/enums';

import { useLanguage } from './useLanguage';

export function useEnum<K extends keyof EnumsStore>(
  name: K,
  fallback: EnumsStore[K]['data'] = [],
): [EnumsStore[K]['data'], EnumsStore[K]['loading'], EnumsStore[K]['error']] {
  const dispatch = useDispatch();
  const resource = useSelector((store: ReduxState) => store.enums[name], shallowEqual);

  useEffect(() => {
    if (resource?.data || resource?.loading || resource?.error) return;
    dispatch(loadEnum(null, name));
  }, [resource]);

  return [resource?.data ?? fallback, resource?.loading, resource?.error];
}

export function useEnumPreload(enums: (keyof EnumsStore)[] = []) {
  const language = useLanguage();
  const dispatch = useDispatch();
  const loaded = useSelector((store: ReduxState) =>
    enums.map(it => store.enums[it]?.loading).every(it => it === false),
  );

  useEffect(() => {
    if (!enums.length || loaded) return;
    dispatch(loadEnum(language, ...enums));
  }, []);

  if (!enums.length) return true;
  return loaded;
}
